import PlaygroundParametersPanel from "./PlaygroundParametersPanel";
import { Plus, X, ChevronDown } from "lucide-react";
import { usePlayground } from "../../contexts/PlaygroundContext";
import { MAX_USER_MESSAGES } from "./types";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";
import { cn } from "../../lib/utils";

const textareaClass =
    "flex w-full rounded-md border border-input bg-background px-4 py-3 text-[15px] leading-relaxed transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:border-ring disabled:cursor-not-allowed disabled:opacity-50 resize-none";

export default function PlaygroundInputArea() {
    const { state, setSystemPrompt, setUserMessage, addPrompt, removePrompt } = usePlayground();

    const isComparePrompts = state.mode === 'compare-prompts';
    const canAddMore = state.userMessages.length < MAX_USER_MESSAGES;
    const canRemove = state.userMessages.length > 1;

    if (isComparePrompts) {
        return (
            <TooltipProvider>
                <div className="flex flex-col gap-3">
                    <Label className="text-[13px] font-semibold">Prompts</Label>

                    {state.userMessages.map((userMessage, index) => (
                        <div
                            key={index}
                            role="group"
                            aria-label={`Prompt ${index + 1}`}
                            className="flex flex-col gap-4 rounded-md border border-border bg-muted/20 p-4"
                        >
                            <div className="-mx-4 -mt-4 flex items-center justify-between border-b border-border bg-muted/60 px-4 py-3">
                                <span className="text-sm font-semibold text-foreground">Prompt {index + 1}</span>
                                {canRemove && (
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <button
                                                type="button"
                                                onClick={() => removePrompt(index)}
                                                disabled={state.isGenerating}
                                                aria-label="Remover prompt"
                                                className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive disabled:opacity-50"
                                            >
                                                <X className="h-3.5 w-3.5" />
                                            </button>
                                        </TooltipTrigger>
                                        <TooltipContent>Remover prompt</TooltipContent>
                                    </Tooltip>
                                )}
                            </div>
                            <div className="flex flex-col gap-1">
                                <span className="text-sm font-medium text-foreground">System (opcional)</span>
                                <textarea
                                    aria-label={`Instruções do sistema do prompt ${index + 1}`}
                                    rows={2}
                                    placeholder="Você é um assistente útil..."
                                    value={state.systemPrompts[index] || ''}
                                    onChange={(e) => setSystemPrompt(index, e.target.value)}
                                    disabled={state.isGenerating}
                                    className={cn(textareaClass)}
                                />
                            </div>
                            <div className="flex flex-col gap-1">
                                <span className="text-sm font-medium text-foreground">Mensagem do usuário</span>
                                <textarea
                                    aria-label={`Mensagem do prompt ${index + 1}`}
                                    rows={4}
                                    placeholder="Digite sua mensagem aqui..."
                                    value={userMessage}
                                    onChange={(e) => setUserMessage(index, e.target.value)}
                                    disabled={state.isGenerating}
                                    className={cn(textareaClass)}
                                />
                            </div>
                            <details className="overflow-hidden rounded-md border border-border">
                                <summary className="flex cursor-pointer items-center justify-between gap-2 bg-muted/40 px-3 py-3 text-sm font-medium">
                                    Parâmetros de geração
                                </summary>
                                <div className="border-t border-border">
                                    <PlaygroundParametersPanel promptIndex={index} />
                                </div>
                            </details>
                        </div>
                    ))}

                    {canAddMore && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={addPrompt}
                            disabled={state.isGenerating}
                            className="self-start"
                        >
                            <Plus className="h-4 w-4" />
                            Adicionar Prompt
                        </Button>
                    )}
                </div>
            </TooltipProvider>
        );
    }

    return (
        <div className="flex flex-col gap-4">
            <details className="group overflow-hidden rounded-md border border-border" open={!!state.systemPrompts[0]}>
                <summary className="flex cursor-pointer list-none items-center justify-between gap-3 bg-muted/60 px-4 py-3 text-sm font-semibold [&::-webkit-details-marker]:hidden">
                    Instruções do sistema <span className="ml-auto text-xs text-muted-foreground">Opcional</span>
                    <ChevronDown className="h-4 w-4 text-muted-foreground group-open:rotate-180" />
                </summary>
                <div className="border-t border-border p-4">
                    <textarea
                        aria-label="Instruções do sistema"
                        rows={3}
                        placeholder="Você é um assistente útil e prestativo..."
                        value={state.systemPrompts[0] || ''}
                        onChange={(e) => setSystemPrompt(0, e.target.value)}
                        disabled={state.isGenerating}
                        className={textareaClass}
                    />
                </div>
            </details>
            <div className="overflow-hidden rounded-md border border-border">
                <Label htmlFor="playground-user-message" className="block border-b border-border bg-muted/60 px-4 py-3 text-sm font-semibold">Mensagem do usuário</Label>
                <div className="p-4">
                <textarea
                    id="playground-user-message"
                    rows={6}
                    placeholder="Digite sua mensagem aqui..."
                    value={state.userMessages[0] || ''}
                    onChange={(e) => setUserMessage(0, e.target.value)}
                    disabled={state.isGenerating}
                    required
                    className={cn(textareaClass)}
                />
                </div>
            </div>
        </div>
    );
}
